import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import lodash from 'lodash';
import { Skeleton } from 'antd';

import NotificationRow from './NotificationRow';
import {
  getNotificationsRequest,
  setNotificationsReadRequest,
} from '../../actionCreators/notificationActions';
import { postNewMemberRequest } from '../../actionCreators/groupActions';
import { getUserBadgesRequest } from '../../actionCreators/userProfile';
import { logFirebaseEvent } from '../../firebase/analytics';
import GridContainer from '../global/GridContainer/GridContainer';

const LIMIT = 15;

const NotificationComponent = (props) => {
  const [state, setState] = useState({
    showing: LIMIT,
  });

  useEffect(() => {
    props.getUserBadges();
    props.getNotifications({ page: 1 });
    logFirebaseEvent({
      event: 'app_notifications',
      action: 'load',
    });
    return () => {
      markAllAsRead(false);
    };
  }, []);

  const markAllAsRead = (skipFetch) => {
    const ids = [];
    props.items.forEach((item) => {
      if (!item.is_read) {
        item.meta &&
          !item.meta.length &&
          logFirebaseEvent({
            event: 'points_goal',
          });
        item.notification.indexOf('badge') !== -1 &&
          logFirebaseEvent({
            event: 'badge',
          });
        ids.push(item.id);
      }
    });

    props.setNotificationsRead({ ids, skipFetch });
  };

  const prepNotifications = (items) => {
    const read = [];
    const notRead = [];

    for (let i = 0; i < state.showing; i++) {
      if (!items[i]) continue;
      const copy = { ...items[i] };

      // Find proper badge type
      for (const meta of items[i].meta || []) {
        if (meta.data.type !== 'badges') continue;

        const badge = lodash.find(props.badges, { id: +meta.data.id });
        if (badge) copy.badge = badge;
        break;
      }

      // Split items on read and unread
      if (items[i].is_read) read.push(copy);
      else notRead.push(copy);
    }

    return [read, notRead];
  };

  const getMoreNotifications = () => {
    setState((state) => {
      if (state.showing >= props.items.length) props.getNotifications();

      return {
        showing: state.showing + LIMIT,
      };
    });
  };

  const [read, notRead] = prepNotifications(props.items);

  const showViewMore =
    !props.loading && (state.showing < props.items.length || props.currentPage < props.totalPages);

  const renderLoading = () => {
    if (!props.loading) return null;

    return (
      <div className="notifications-holder">
        <Skeleton paragraph={false} avatar />
        <Skeleton paragraph={false} avatar />
        <Skeleton paragraph={false} avatar />
        <Skeleton paragraph={false} avatar />
      </div>
    );
  };

  const renderNotRead = (items) => {
    if (!items.length) return null;

    return (
      <>
        <div
          className="notifications-button align-self-end"
          onClick={() => {
            logFirebaseEvent({
              event: 'app_notifications',
              action: 'mark_all_read',
            });
            markAllAsRead(false);
          }}
        >
          MARK ALL AS READ
        </div>

        <span className="notifications-title">
          Unread
          <span className="notifications-title-badge">{props.unreadCount}</span>
        </span>

        <div className="notifications-holder">
          {items.map((notification) => {
            return (
              <NotificationRow
                token={props.token}
                acceptNewMember={props.acceptNewMember}
                key={`notread:${notification.id}`}
                notification={notification}
              />
            );
          })}
        </div>
      </>
    );
  };

  const renderRead = (items) => {
    if (!items.length) return null;

    return (
      <>
        <span className="notifications-title mt-4">Older</span>

        <div className="notifications-holder">
          {items.map((notification) => {
            return (
              <NotificationRow
                token={props.token}
                acceptNewMember={props.acceptNewMember}
                key={`read:${notification.id}`}
                notification={notification}
              />
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="notifications">
      <GridContainer hideNotifications={true}>
        <p className="notifications-header-text">Notifications</p>
        <div className="notifications-content">
          {renderLoading()}
          {renderNotRead(notRead)}
          {renderRead(read)}

          {showViewMore && (
            <div className="notifications-button align-self-center" onClick={getMoreNotifications}>
              VIEW MORE
            </div>
          )}
        </div>
      </GridContainer>
    </div>
  );
};

const Notification = (props) => {
  const state = useSelector((state) => ({
    ...state.notification,
    badges: state.currentUser.badges.userBadges || [],
    token: state.currentUser.signInData.key,
  }));

  const dispatch = useDispatch();

  const actions = {
    getNotifications: (payload) => dispatch(getNotificationsRequest(payload)),
    setNotificationsRead: (payload) => dispatch(setNotificationsReadRequest(payload)),
    getUserBadges: () => dispatch(getUserBadgesRequest()),
    acceptNewMember: (payload) => dispatch(postNewMemberRequest(payload)),
  };

  return <NotificationComponent {...props} {...state} {...actions} />;
};

export default Notification;
